import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { EnvironmentName, Me, OrgSummary, Project, Region } from "../api/schemas";
import { prefs } from "./storage";

export interface Workspace {
  me: Me;
  org: OrgSummary;
  project: Project;
  region: Region | undefined;
  environment: EnvironmentName;
  setEnvironment: (env: EnvironmentName) => void;
}

const WorkspaceContext = createContext<Workspace | null>(null);

const envKey = (projectId: string) => `atx-env:${projectId}`;
export const LAST_PROJECT_KEY = "atx-last-project";

export function WorkspaceProvider({
  me,
  org,
  project,
  region,
  children,
}: Omit<Workspace, "environment" | "setEnvironment"> & { children: ReactNode }) {
  const initial = (): EnvironmentName => {
    const saved = prefs.get(envKey(project.id));
    if (saved && project.environments.some((e) => e.name === saved)) return saved as EnvironmentName;
    return project.environments.find((e) => e.is_default)?.name ?? "production";
  };
  const [state, setState] = useState<{ projectId: string; env: EnvironmentName }>(() => ({
    projectId: project.id,
    env: initial(),
  }));
  // Switching project resets to that project's remembered environment.
  const environment = state.projectId === project.id ? state.env : initial();

  const setEnvironment = useCallback(
    (env: EnvironmentName) => {
      prefs.set(envKey(project.id), env);
      setState({ projectId: project.id, env });
    },
    [project.id],
  );

  const value = useMemo(
    () => ({ me, org, project, region, environment, setEnvironment }),
    [me, org, project, region, environment, setEnvironment],
  );
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Workspace {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace outside a project route");
  return ctx;
}
